/**
 * TerminalOutput - json-render component
 * 
 * Displays command execution output in a terminal-style format.
 * Supports expandable output and status indicators.
 */

import { useEffect, useRef, useState } from 'react';
import {
    Terminal,
    Loader2,
    CheckCircle2,
    XCircle,
    ChevronDown,
    ChevronRight,
    Copy,
    Check
} from 'lucide-react';
import { cn } from '@pagespeedify/ui';

export interface TerminalOutputProps {
    command: string;
    output?: string[];
    status: 'running' | 'success' | 'error';
    expandable?: boolean;
    defaultExpanded?: boolean;
    autoScroll?: boolean;
    className?: string;
    bodyClassName?: string;
}

interface StatusConfig {
    icon: typeof Loader2;
    color: string;
    borderColor: string;
    animate?: boolean;
}

const statusConfig: Record<TerminalOutputProps['status'], StatusConfig> = {
    running: {
        icon: Loader2,
        color: 'text-blue-400',
        borderColor: 'border-blue-500',
        animate: true
    },
    success: {
        icon: CheckCircle2,
        color: 'text-green-400',
        borderColor: 'border-green-500'
    },
    error: {
        icon: XCircle,
        color: 'text-red-400',
        borderColor: 'border-red-500'
    }
};

export default function TerminalOutput({
    command,
    output = [],
    status,
    expandable = true,
    defaultExpanded = true,
    autoScroll = false,
    className,
    bodyClassName
}: TerminalOutputProps) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [copied, setCopied] = useState(false);
    const outputRef = useRef<HTMLDivElement | null>(null);

    const config = statusConfig[status];
    const StatusIcon = config.icon;

    useEffect(() => {
        if (!autoScroll || !isExpanded || !outputRef.current) return;
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }, [autoScroll, isExpanded, output]);

    const handleCopy = async (e: globalThis.React.MouseEvent) => {
        e.stopPropagation();
        const textToCopy = output && output.length > 0 ? output.join('\n') : command;
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className={cn(
            "rounded-lg overflow-hidden border-l-2",
            config.borderColor,
            "bg-slate-900 dark:bg-slate-950",
            className
        )}>
            {/* Header with command */}
            <div
                className={cn(
                    "flex items-center gap-2 px-3 py-2 bg-slate-800",
                    expandable && output.length > 0 && "cursor-pointer hover:bg-slate-750"
                )}
                onClick={() => expandable && output.length > 0 && setIsExpanded(!isExpanded)}
            >
                {expandable && output.length > 0 && (
                    <span className="text-slate-500">
                        {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </span>
                )}
                <Terminal className="h-4 w-4 text-slate-400" />
                <code className="flex-1 text-sm font-mono text-slate-300 truncate">
                    $ {command}
                </code>
                <button
                    type="button"
                    className="h-6 w-6 p-1 text-slate-400 hover:text-slate-200 rounded"
                    onClick={handleCopy}
                >
                    {copied ? (
                        <Check className="h-3 w-3" />
                    ) : (
                        <Copy className="h-3 w-3" />
                    )}
                </button>
                <StatusIcon
                    className={cn(
                        "h-4 w-4 shrink-0",
                        config.color,
                        config.animate && "animate-spin"
                    )}
                />
            </div>

            {/* Output lines */}
            {isExpanded && output.length > 0 && (
                <div
                    ref={outputRef}
                    data-testid="terminal-output-body"
                    className={cn(
                        "px-3 py-2 overflow-y-auto no-scrollbar",
                        bodyClassName || "max-h-64"
                    )}
                >
                    <pre className="text-xs font-mono text-slate-400 whitespace-pre-wrap">
                        {output.map((line, i) => {
                            const lineClass = line.toLowerCase().includes('error')
                                ? 'text-red-400'
                                : line.toLowerCase().includes('warning')
                                    ? 'text-yellow-400'
                                    : line.toLowerCase().includes('success')
                                        ? 'text-green-400'
                                        : '';
                            return (
                                <div key={i} className={lineClass}>
                                    {line}
                                </div>
                            );
                        })}
                    </pre>
                </div>
            )}
        </div>
    );
}
